// Lightweight client diagnostics for hard-to-reproduce, device-specific failures
// (chiefly the empty branch dropdown in the LINE in-app browser).
//
// Design notes:
//  - No store / api-client imports. It posts straight to /api/order/save_log with a
//    bare fetch so a failure in the app's own plumbing (interceptors, auth, the thing
//    we're trying to diagnose) can't take the logger down with it, and so there is no
//    circular import with the store module that calls it.
//  - Never throws into app code: every path is wrapped.
//  - Resilient delivery: a failed send is buffered in localStorage and retried on the
//    next page load, so a session that couldn't reach the server still reports later.
//  - Privacy: we log a random client session id, never the JWT.
import liff from '@line/liff'

const API_BASE = process.env.VUE_APP_API_BASE_URL || ''
const SAVE_LOG_URL = `${API_BASE}/api/order/save_log`
const QUEUE_KEY = 'diag_queue'
const MAX_QUEUE = 50

const BUILD_ID = process.env.VUE_APP_BUILD_ID || 'unknown'
const BUILD_TIME = process.env.VUE_APP_BUILD_TIME || ''

const safe = (fn, fallback = null) => { try { return fn() } catch (e) { return fallback } }

// One id per page load, so multiple events from the same session can be grouped.
const SESSION_ID = safe(
  () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
  'sess',
)

// Tracks "first OK per session" sampling keys and js_error dedupe signatures.
const onceSeen = new Set()

let lastUserId = 0
export const setUserId = (id) => { if (id) lastUserId = id }

const liffInfo = () => ({
  isInClient: safe(() => liff.isInClient()),
  os: safe(() => liff.getOS()),
  lineVersion: safe(() => liff.getLineVersion()),   // the LINE app version
  liffVersion: safe(() => liff.getVersion()),       // the LIFF SDK version
  appLanguage: safe(() => liff.getAppLanguage()),
})

const storageOk = () => {
  try {
    localStorage.setItem('__diag_probe__', '1')
    localStorage.removeItem('__diag_probe__')
    return true
  } catch (e) {
    return false // private-mode / restricted webview -> no cache fallback is possible
  }
}

const buildContext = (extra) => ({
  build: BUILD_ID,
  buildTime: BUILD_TIME,
  sid: SESSION_ID,
  liff: liffInfo(),
  screen: safe(() => `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`),
  lang: safe(() => navigator.language),
  tz: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
  online: safe(() => navigator.onLine),
  conn: safe(() => (navigator.connection && navigator.connection.effectiveType) || null),
  storageOk: storageOk(),
  ...(extra || {}),
})

// ---- resilient delivery -----------------------------------------------------
const readQueue = () => safe(() => JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'), [])
const writeQueue = (arr) => safe(() => localStorage.setItem(QUEUE_KEY, JSON.stringify(arr.slice(-MAX_QUEUE))))
const enqueue = (payload) => { const q = readQueue(); q.push(payload); writeQueue(q) }

const send = (payload) =>
  fetch(SAVE_LOG_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true, // survives navigation/unload, allows JSON body + headers
  }).then((r) => { if (!r.ok) throw new Error('save_log ' + r.status); return true })

const toPayload = (event, info, userId) => ({
  user_id: userId || lastUserId || 0,
  session_key: SESSION_ID,          // client session id, NOT a JWT
  event,
  url: safe(() => window.location.href, ''),
  info: JSON.stringify(info),
})

/**
 * logEvent(event, info, opts)
 *   opts.userId  explicit user id (callers that have it)
 *   opts.once    a string key; only the first event with this key per session is sent
 *               (used for "first OK per session" sampling and error dedupe)
 */
export const logEvent = (event, info, opts = {}) => {
  try {
    if (opts.once) {
      if (onceSeen.has(opts.once)) return
      onceSeen.add(opts.once)
    }
    const payload = toPayload(event, buildContext(info), opts.userId)
    send(payload).catch(() => enqueue(payload))
  } catch (e) { /* diagnostics must never break the app */ }
}

// Resend anything buffered from a previous (failed) session. Call once at startup.
export const flushQueue = () => {
  try {
    const q = readQueue()
    if (!q.length) return
    writeQueue([]) // optimistic clear; failures re-enqueue themselves
    q.forEach((payload) => { send(payload).catch(() => enqueue(payload)) })
  } catch (e) { /* ignore */ }
}

// Catch page-breaking JS errors (a prime suspect for "no branch list" on a specific
// LINE/WebKit version where the page silently fails to render). Deduped per session.
export const registerGlobalErrorLogging = () => {
  try {
    window.addEventListener('error', (e) => {
      const sig = `err:${e.message}@${e.filename}:${e.lineno}:${e.colno}`
      if (onceSeen.has(sig)) return
      onceSeen.add(sig)
      logEvent('js_error', {
        kind: 'error',
        message: String(e.message || '').slice(0, 300),
        source: `${e.filename || ''}:${e.lineno || 0}:${e.colno || 0}`,
        stack: safe(() => (e.error && e.error.stack ? String(e.error.stack).slice(0, 600) : null)),
      })
    })
    window.addEventListener('unhandledrejection', (e) => {
      const reason = e && e.reason
      const msg = safe(() => (reason && (reason.message || reason.toString())) || String(reason), 'unknown')
      const sig = 'rej:' + String(msg).slice(0, 120)
      if (onceSeen.has(sig)) return
      onceSeen.add(sig)
      logEvent('js_error', {
        kind: 'unhandledrejection',
        message: String(msg).slice(0, 300),
        stack: safe(() => (reason && reason.stack ? String(reason.stack).slice(0, 600) : null)),
      })
    })
  } catch (e) { /* ignore */ }
}
